import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import useOffer from 'hooks/Offers/useOffer'
import LesaPurchaseSummaryItineraryDates from './Common/LesaPurchaseSummaryItineraryDates'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import LesaPurchaseSummarySkeleton from './Common/LesaPurchaseSummarySkeleton'
import Action from '../../Chat/MessageMeta/Action'
import { filterActions } from '../../../utils/actionsUtils'
import FinePrintModal from '../../Chat/MessageMeta/FinePrintModal'
import LesaPurchaseSummaryTitle from './Common/LesaPurchaseSummaryTitle'
import { useAppDispatch } from 'hooks/reduxHooks'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'

interface Props {
  order: App.Order
  isPastOrder: boolean
  isCurrentOrder: boolean
  hideSelect?: boolean
  actions?: Array<App.ChatMessageMetaContent>
  payload?: App.ChatMessageMetaPayload
  componentsMap: Map<string | undefined, App.ChatMessageMetaContent>
  canEdit: boolean
}

export default function LesaVillaPurchaseSummary(props: Props) {
  const { order, isPastOrder, hideSelect, actions, payload, componentsMap, isCurrentOrder, canEdit } = props
  const intl = useIntl()

  const dispatch = useAppDispatch()

  let orderItems = order.items.filter(item => item.status !== 'cancelled')

  if (orderItems.length === 0) {
    orderItems = order.items.slice(-1)
  }

  const item = orderItems[0]
  const [offer] = useOffer<App.VillaOffer>(item?.offerId)

  const handleSelectClick = useCallback(() => {
    dispatch(addChatMessage('', { action: 'selectOrder', payload: { id_orders: order.id, isCurrentOrder, canEditOrder: canEdit } }))
  }, [dispatch, order.id, isCurrentOrder, canEdit])

  if (!offer) {
    return <LesaPurchaseSummarySkeleton />
  }

  const locationLabel = offer.locationHeading && offer.locationSubheading
    ? intl.formatMessage(
        {
          id: 'pPjqnkpqQ+',
          defaultMessage: '{locationHeading}, {locationSubheading}',
          description: 'Support assistant villa purchase summary location separated by comma',
        },
        { locationHeading: offer.locationHeading, locationSubheading: offer.locationSubheading },
      )
    : (offer.locationHeading || offer.locationSubheading)

  const orderActions = actions?.filter(action => filterActions(action, isPastOrder, ['listInclusions']))

  return (
    <>
      {(!!componentsMap?.get('listOrders') || !!componentsMap?.get('selectOrder')) && (
        <LesaPurchaseSummaryContainer>
          <LesaPurchaseSummaryHeader order={order}>
            <BodyTextBlock lineClamp={2} variant="small" startIcon={<LineBedIcon size="XS" />}>{intl.formatMessage({
              id: 'N2TwTejB4Q',
              defaultMessage: 'Hotel',
              description: 'Category label for villa/hotel purchase',
            })}
            </BodyTextBlock>
            {!isPastOrder && <StatusLabel variant="confirmation">{intl.formatMessage({
              id: 'n0MmC/miju',
              defaultMessage: 'Upcoming',
              description: 'Status label for upcoming villa purchase',
            })}
            </StatusLabel>}
            {isPastOrder && <StatusLabel variant="pending">{intl.formatMessage({
              id: 'dh0zaRr/7Y',
              defaultMessage: 'Past',
              description: 'Status label for past villa purchase',
            })}
            </StatusLabel>}
          </LesaPurchaseSummaryHeader>
          <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={16}>
            <Group direction="vertical" gap={4}>
              <CaptionTextBlock
                variant="medium"
                colour="neutral-three"
              >{locationLabel}
              </CaptionTextBlock>
              <LesaPurchaseSummaryTitle orderId={order.id}>
                {offer.property.name}
              </LesaPurchaseSummaryTitle>
              {orderItems?.map(item => (
                <div key={item.id}>
                  <LesaPurchaseSummaryItineraryDates
                    key={item.id}
                    status={item.status}
                    startDate={item.reservation?.startDate}
                    endDate={item.reservation?.endDate}
                    duration={intl.formatMessage({
                      id: 'mIJzc4qD85',
                      defaultMessage: '{count, plural, one {# night} other {# nights}}',
                      description: 'Number of nights for villa stay duration',
                    }, { count: item.duration ?? 0 })}
                    bookingNumber={item.bookingNumber}
                    showReservationDates
                  />
                  <BodyTextBlock variant="small" colour="neutral-three">
                    {formatOccupantsShort(item.reservation)}
                  </BodyTextBlock>
                </div>
              ))}
            </Group>
            {!hideSelect &&
              <TextButton onClick={handleSelectClick} fit="mobile-full-width" size="small" kind="secondary">
                {intl.formatMessage({
                  id: '85krelvShv',
                  defaultMessage: 'Select',
                  description: 'Button to select a villa purchase',
                })}
              </TextButton>}
          </Group>
        </LesaPurchaseSummaryContainer>
      )}
      {!!componentsMap?.get('fine-print-modal') && (
        <FinePrintModal offer={offer} />
      )}
      {!!orderActions?.length &&
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          {orderActions.map((action) => (
            <Action key={action.action?.key} action={action} payload={payload} />
          ))}
        </Group>}
    </>
  )
}
